import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Video, VideoOff, Maximize2, Minimize2, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSession } from '../../hooks/useSession';
import { StepContent } from '../counseling/session';
import MeetingEmbed from './MeetingEmbed';
import api from '../../services/api';

export default function VirtualSessionRoom() {
  const { appointmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetingData, setMeetingData] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [showNotesPanel, setShowNotesPanel] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Use existing session hook for note-taking (counselor only)
  const isCounselor = user?.role === 'counselor' || user?.role === 'hod';

  const sessionHook = useSession(); // Only used by counselors
  const {
    sessionData,
    activeStep,
    handleFormSubmit,
    handleFormChange,
    isStepComplete
  } = sessionHook || {};

  // Fetch meeting details on mount
  useEffect(() => {
    fetchMeetingDetails();
  }, [appointmentId]);

  // Log meeting join
  useEffect(() => {
    if (meetingData && !loading) {
      logMeetingJoin();
    }

    // Log meeting leave on unmount
    return () => {
      if (meetingData) {
        logMeetingLeave();
      }
    };
  }, [meetingData, loading]);

  const fetchMeetingDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/virtual-sessions/appointments/${appointmentId}/meeting`);

      if (response.data.status === 'success') {
        setMeetingData(response.data.data.meeting);
        setAppointment(response.data.data.appointment);
      }
    } catch (err) {
      console.error('Error fetching meeting details:', err);
      setError(
        err.response?.data?.message ||
        'Failed to load meeting. Please check if you have access to this virtual session.'
      );
    } finally {
      setLoading(false);
    }
  };

  const logMeetingJoin = async () => {
    try {
      await api.post(`/virtual-sessions/appointments/${appointmentId}/meeting/join`);
      console.log('Meeting join logged');
    } catch (err) {
      console.error('Error logging meeting join:', err);
    }
  };

  const logMeetingLeave = async () => {
    try {
      await api.post(`/virtual-sessions/appointments/${appointmentId}/meeting/leave`);
      console.log('Meeting leave logged');
    } catch (err) {
      console.error('Error logging meeting leave:', err);
    }
  };

  const handleEndMeeting = async () => {
    if (window.confirm('Are you sure you want to end this meeting for all participants?')) {
      try {
        await api.post(`/virtual-sessions/appointments/${appointmentId}/meeting/end`);
        navigate(`/${user.role}/appointments/${appointmentId}`);
      } catch (err) {
        console.error('Error ending meeting:', err);
        alert('Failed to end meeting');
      }
    }
  };

  const toggleNotesPanel = () => {
    setShowNotesPanel(!showNotesPanel);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500" />
        <p className="ml-3 text-gray-600">Loading virtual session...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Unable to Join Meeting</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button
                onClick={() => navigate(`/${user.role}/appointments`)}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Back to Appointments
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-4 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Video className="h-5 w-5 text-uzima-maroon-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Virtual Counseling Session</h2>
            <p className="text-sm text-gray-600">
              {appointment?.date && new Date(appointment.date).toLocaleDateString()} at {appointment?.timeSlot}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {isCounselor && (
            <>
              <button
                onClick={toggleNotesPanel}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md flex items-center"
                title={showNotesPanel ? "Hide notes panel" : "Show notes panel"}
              >
                {showNotesPanel ? <Minimize2 className="h-4 w-4 mr-1" /> : <Maximize2 className="h-4 w-4 mr-1" />}
                {showNotesPanel ? 'Hide Notes' : 'Show Notes'}
              </button>

              <button
                onClick={handleEndMeeting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
              >
                <VideoOff className="h-4 w-4 mr-2" />
                End Meeting
              </button>
            </>
          )}

          <button
            onClick={() => navigate(`/${user.role}/appointments/${appointmentId}`)}
            className="p-2 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-100"
            title="Leave meeting"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Meeting Area */}
        <div className={`${isCounselor && showNotesPanel ? 'w-2/3' : 'w-full'} transition-all duration-300 h-full overflow-hidden relative`}>
          <MeetingEmbed
            meetingLink={meetingData?.meetingLink}
            roomConfig={meetingData?.roomConfig}
            userName={user?.fullName || user?.username}
            userEmail={user?.email}
          />
        </div>

        {/* Notes Panel (Counselor Only) */}
        {isCounselor && showNotesPanel && (
          <div className="w-1/3 bg-white border-l shadow-lg overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 z-10">
              <h3 className="font-semibold text-gray-800">Session Notes</h3>
              <p className="text-sm text-gray-600">
                Client: {appointment?.client?.fullName || appointment?.client?.username}
              </p>
            </div>

            <div className="p-4">
              {sessionData ? (
                <StepContent
                  activeStep={activeStep}
                  sessionData={sessionData}
                  onSubmit={handleFormSubmit}
                  isNewClient={sessionData?.record?.sessionNumber === 1}
                  validSteps={sessionData?.record?.sessionNumber === 1
                    ? ['consent', 'intake', 'mentalStatus']
                    : ['record']
                  }
                  actualSessionId={sessionData?._id}
                  showSafetyForm={false}
                  showTerminationForm={false}
                />
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Session notes will appear here</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Initialize the session to start taking notes
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Info (Student View) */}
      {!isCounselor && (
        <div className="bg-white border-t px-4 py-3">
          <p className="text-sm text-gray-600 text-center">
            Your counselor is taking notes during this session. You'll receive a summary after the session ends.
          </p>
        </div>
      )}
    </div>
  );
}
